'use client';

import { useCallback, useMemo, useState } from 'react';
import { defaultVariants } from './DemandCalculator.styles';
import { Container } from '@/components/primitives/Container/Container';
import RichTextWrapper from '@/helpers/Wrappers/RichTextWrapper/RichTextWrapper';
import { toPascalCase } from '@/utils/string-utils';
import { getCSLPAttributes } from '@/utils/type-guards';
import { IBaseComponentProps, WithMetadata } from '@/lib/types';
import { IDemandCalculatorPageComponents } from '@/.generated';
import ImageWrapper from '@/helpers/Wrappers/ImageWrapper/ImageWrapper';
import { toEnhancedImage } from '@/utils/image-utils';
import { cn } from 'tailwind-variants';

type DemandCalculatorProps = WithMetadata<IBaseComponentProps> &
  IDemandCalculatorPageComponents['demand_calculator'];

type Device = NonNullable<DemandCalculatorProps['devices_list']>[number];
type Scenario = 'summer' | 'winter';

// Parses an optional kW string (e.g. "4.00") to a number, falling back to 0 on invalid input
const parseKw = (value?: string): number => {
  const n = parseFloat(value ?? '');
  return Number.isNaN(n) ? 0 : n;
};

const Default = (props: DemandCalculatorProps) => {
  const {
    componentName,
    _metadata,
    inline_navigation_id,
    hide_component,
    summer_images,
    winter_images,
    devices_list,
    clear_button_label,
    total_demand_label,
    foot_note,
    $: cslp,
  } = props;

  const {
    base,
    seasonButtonsRow,
    seasonButtonsWrapper,
    seasonButton,
    seasonButtonImg,
    tableWrapper,
    table,
    kwInput,
    totalInput,
    textboxTitle,
    resetButton,
    footNote,
  } = defaultVariants();

  const devices: Device[] = useMemo(() => devices_list ?? [], [devices_list]);

  // device_id -> active flag; absent keys are implicitly false
  const [active, setActive] = useState<Record<string, boolean>>({});
  // The currently active scenario, or null when neither button is pressed
  const [scenario, setScenario] = useState<Scenario | null>(null);

  // Sum of kW values for every currently active device
  const totalKw = useMemo(
    () =>
      devices.reduce((sum, d) => (active[d.device_id] ? sum + parseKw(d.device_kw_usage) : sum), 0),
    [active, devices]
  );

  // Toggles a single device, enforcing replaces_group mutex on activation
  const toggleDevice = useCallback(
    (device: Device, on: boolean) => {
      setActive((prev) => {
        const next = { ...prev, [device.device_id]: on };
        if (on && device.replaces_group) {
          devices.forEach((d) => {
            if (d.device_id !== device.device_id && d.replaces_group === device.replaces_group) {
              next[d.device_id] = false;
            }
          });
        }
        return next;
      });
    },
    [devices]
  );

  // Picks the season-compatible devices, honoring replaces_group (first wins per group)
  const computeScenarioState = useCallback(
    (target: Scenario): Record<string, boolean> => {
      const seasonLabel = target === 'summer' ? 'Summer' : 'Winter';
      const next: Record<string, boolean> = {};
      const usedGroups = new Set<string>();
      devices.forEach((d) => {
        if (!d.device_compatible_season?.includes(seasonLabel)) return;
        if (d.replaces_group && usedGroups.has(d.replaces_group)) return;
        next[d.device_id] = true;
        if (d.replaces_group) usedGroups.add(d.replaces_group);
      });
      return next;
    },
    [devices]
  );

  // Resets every device and any active scenario
  const handleClearAll = useCallback(() => {
    setActive({});
    setScenario(null);
  }, []);

  // Scenario button click: toggle off if already active, otherwise apply the new scenario
  const handleScenarioClick = useCallback(
    (target: Scenario) => {
      if (scenario === target) {
        handleClearAll();
      } else {
        setActive(computeScenarioState(target));
        setScenario(target);
      }
    },
    [scenario, computeScenarioState, handleClearAll]
  );

  // Asset for the current state of each season button
  const summerAsset =
    scenario === 'summer' ? summer_images?.on_state_image : summer_images?.off_state_image;
  const winterAsset =
    scenario === 'winter' ? winter_images?.on_state_image : winter_images?.off_state_image;

  const summerImage = toEnhancedImage(summerAsset, {
    alternateText: 'Summer Images',
    fallbackWidth: 200,
    fallbackHeight: 200,
    cslp: cslp?.summer_images,
  });
  const winterImage = toEnhancedImage(winterAsset, {
    alternateText: 'Winter Images',
    fallbackWidth: 200,
    fallbackHeight: 200,
    cslp: cslp?.winter_images,
  });

  const summerActive = scenario === 'summer';
  const winterActive = scenario === 'winter';

  if (hide_component) return null;

  return (
    <Container componentName={componentName} metaId={_metadata?.uid} id={inline_navigation_id}>
      <div className={base()}>
        <div className={seasonButtonsRow()}>
          <div className={seasonButtonsWrapper()}>
            <button
              className={seasonButton()}
              aria-pressed={summerActive}
              aria-label="Toggle summer scenario"
              onClick={() => handleScenarioClick('summer')}
            >
              <ImageWrapper image={summerImage} imageClassName={seasonButtonImg()} />
            </button>
          </div>
          <div className={seasonButtonsWrapper()}>
            <button
              className={seasonButton()}
              aria-pressed={winterActive}
              aria-label="Toggle winter scenario"
              onClick={() => handleScenarioClick('winter')}
            >
              <ImageWrapper image={winterImage} imageClassName={seasonButtonImg()} />
            </button>
          </div>
        </div>

        <div className={tableWrapper()}>
          <table className={table()}>
            <tbody>
              {devices.map((device, idx) => (
                <DeviceRow
                  key={`${idx}-${device.device_id}`}
                  device={device}
                  isOn={!!active[device.device_id]}
                  onToggle={toggleDevice}
                  kwInputClass={kwInput()}
                />
              ))}
              <tr>
                <td />
                <td>
                  <button
                    type="button"
                    className={resetButton()}
                    onClick={handleClearAll}
                    {...getCSLPAttributes(cslp?.clear_button_label)}
                  >
                    {clear_button_label || 'Clear all'}
                  </button>
                </td>
                <td className={textboxTitle()}>
                  <span {...getCSLPAttributes(cslp?.total_demand_label)}>
                    {total_demand_label || 'Total demand'}
                  </span>
                  <br />
                  <input
                    type="text"
                    readOnly
                    tabIndex={-1}
                    value={totalKw > 0 ? `${totalKw.toFixed(2)} kW` : ''}
                    placeholder={`${totalKw.toFixed(2)} kW`}
                    className={cn(kwInput(), totalInput())}
                    aria-label={total_demand_label || 'Total demand'}
                    autoComplete="off"
                    data-lpignore="true"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {foot_note && (
          <RichTextWrapper
            content={foot_note}
            cslpAttribute={cslp?.foot_note}
            className={footNote()}
          />
        )}
      </div>
    </Container>
  );
};

// A single device row: name cell + custom toggle + read-only kW display
const DeviceRow = ({
  device,
  isOn,
  onToggle,
  kwInputClass,
}: {
  device: Device;
  isOn: boolean;
  onToggle: (device: Device, on: boolean) => void;
  kwInputClass: string;
}) => {
  const kwDisplay = isOn && device.device_kw_usage ? `${device.device_kw_usage} kW` : '';
  // Stable handler reference per row so CustomToggle's onChange doesn't churn
  const handleChange = useCallback((on: boolean) => onToggle(device, on), [device, onToggle]);
  return (
    <tr>
      <td {...getCSLPAttributes(device.$?.device_name)}>{device.device_name}</td>
      <td>
        <CustomToggle ariaLabel={device.device_name} checked={isOn} onChange={handleChange} />
      </td>
      <td>
        <input
          type="text"
          readOnly
          tabIndex={-1}
          className={kwInputClass}
          value={kwDisplay}
          placeholder="kW"
          aria-label={`${device.device_name} kilowatt usage`}
          autoComplete="off"
          data-lpignore="true"
        />
      </td>
    </tr>
  );
};

// 3-segment toggle (ON | knob | OFF). Native checkbox semantics, plus Enter support.
const CustomToggle = ({
  ariaLabel,
  checked,
  onChange,
}: {
  ariaLabel?: string;
  checked: boolean;
  onChange: (on: boolean) => void;
}) => {
  // The isOn variant flips the slide position of the inner segment row
  const { switchLabel, switchInput, switchTrack, switchInner, switchOn, switchKnob, switchOff } =
    defaultVariants({ isOn: checked });

  // Native checkboxes only respond to Space — add Enter for parity with the season buttons
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onChange(!checked);
    }
  };

  return (
    <label className={switchLabel()}>
      <input
        type="checkbox"
        className={switchInput()}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        onKeyDown={handleKeyDown}
        aria-label={ariaLabel}
      />
      <span className={switchTrack()}>
        <span className={switchInner()}>
          <span className={switchOn()}>ON</span>
          <span className={switchKnob()} />
          <span className={switchOff()}>OFF</span>
        </span>
      </span>
    </label>
  );
};

const variants = {
  Default,
};

// Routes to the matching variant by component_variant, falling back to Default
export const DemandCalculator = (props: DemandCalculatorProps) => {
  const Component = props.component_variant
    ? variants[toPascalCase(props.component_variant) as keyof typeof variants]
    : Default;
  return <Component {...props} />;
};
